import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './Events.css';
import MapTable from './MapTable'; // Assuming MapTable is generic and can be reused for events
import DeviceConnection from './DeviceConnection';
import { useBle } from './BleContext'; // Ensure you have useBle to manage Bluetooth connections

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoUpload, setAutoUpload] = useState(Cookies.get('autoUploadEvents') === 'true'); // Use a different cookie for autoUpload
  const { requestMapData, uploadMapData } = useBle();
  const [uploadTriggered, setUploadTriggered] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [sentPoints, setSentPoints] = useState([]);
  const [connectionState, setConnectionState] = useState('❌ Not Connected');
  const [mapSource, setMapSource] = useState(''); // New state to track map source

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('https://compass-events-fzjaqefbta-uc.a.run.app');
      console.log('Response data:', response.data);
      setEvents(response.data.data); // Assuming the response has a data property containing the events
      setMapSource('remote'); // Set map source to 'remote' after fetching events
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to fetch events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchMapPoints = async () => {
    try {
      console.log('Requesting map data from device...');
      await requestMapData(
        (totalPoints) => {
          console.log('Starting map data transfer, total points:', totalPoints);
          setEvents([]);
        },
        (currentPoint, totalPoints, point) => {
          setEvents((prevPoints) => [...prevPoints, point]);
        },
        (points) => {
          console.log('Map data received successfully:', points);
          setEvents(points);
          setMapSource('compass'); // Set map source to 'compass' after reading points
        }
      );
    } catch (error) {
      console.error('Error requesting map data:', error);
      setError('Failed to request map data from the device. Please try again.');
    }
  };

  const handleUploadMap = async () => {
    try {
      setSentPoints([]); // Reset sent points when uploading starts
      console.log('Uploading map data to device...');
      await uploadMapData(
        events,
        (currentPoint, totalPoints) => {
          console.log(`Progress: ${currentPoint}/${totalPoints}`);
          setProgress({ current: currentPoint, total: totalPoints });
          setSentPoints((prevSentPoints) => [...prevSentPoints, currentPoint - 1]); // Add current point to sent points
        }
      );
      console.log('Map data uploaded successfully');
    } catch (error) {
      console.error('Error uploading map data:', error);
      setError('Failed to upload map data to the device. Please try again.');
    }
  };

  useEffect(() => {
    if (autoUpload && events.length === 0) {
      fetchEvents();
    }
  }, [autoUpload, events.length]);

  const handleAutoUploadChange = (event) => {
    setAutoUpload(event.target.checked);
    Cookies.set('autoUploadEvents', event.target.checked, { expires: 365 });
  };

  const handleDeviceConnect = () => {
    if (autoUpload && !uploadTriggered) {
      setUploadTriggered(true);
      // Only upload if points are available and the source is not 'compass'
      if (events.length > 0 && mapSource !== 'compass') {
        handleUploadMap();
      }
    }
  };

  const handleDeviceDisconnect = () => {
    setUploadTriggered(false);
  };

  return (
    <div className="events-upload">
    <DeviceConnection
      onConnect={handleDeviceConnect}
      onDisconnect={handleDeviceDisconnect}
      onConnectionStateChange={setConnectionState}
    />
    {connectionState === '✅ Connected' && (
      <div className="device-buttons">
        <button onClick={handleFetchMapPoints} className="active-button" disabled={loading}>
          {loading ? 'Reading...' : 'Read events from compass'}
        </button>
        <button onClick={handleUploadMap} className="active-button" disabled={loading}>
          {loading ? 'Uploading...' : 'Upload events to compass'}
        </button>
      </div>
    )}
    
      <p>
        <label>
          <input type="checkbox" checked={autoUpload} onChange={handleAutoUploadChange} />
          Upload events automatically
        </label>
      </p>
      <button onClick={fetchEvents} className="active-button" disabled={loading}>
        {loading ? 'Fetching...' : 'Fetch Events from Cloud'}
      </button>
        <a href="https://compass-events-fzjaqefbta-uc.a.run.app?format=kml" className="active-link">
          Download KML
        </a>
      {error && <p className="error-message">{error}</p>}
      <MapTable 
        points={events}
        progress={progress}
        sentPoints={sentPoints}
      />
    </div>
  );
};

export default Events;
